import { useRenderStore } from '@/features/design/render/model/render-form.store';
import {
  applyRenderSettingsPatch,
  renderFormPartial,
  renderSettingsSnapshot,
} from '@/features/design/render/lib/render-settings-patch';
import { createLogger } from '@/shared/lib/logger';
import type { RenderMode } from '@/types/render';
import { useAssistantStore, type PendingPatch } from '../model/assistant.store';

const logger = createLogger('Assistant');

/** True when the settings form of this design is the one loaded. */
export function isDesignFormLoaded(designId: string): boolean {
  return useRenderStore.getState().currentContext?.conceptId === designId;
}

/**
 * Apply an `apply_render_settings` patch to the design's settings form, keeping
 * the form as it was for the message's Undo (ADR-001 §3, §5). Returns false
 * when that form is not loaded yet; the caller keeps the patch pending until
 * it is (an `open_design` before the patch is still navigating).
 */
export function applyPatchToForm(pending: PendingPatch): boolean {
  if (!isDesignFormLoaded(pending.designId)) return false;

  const priorMode = useRenderStore.getState().formData.mode;
  // The mode goes first (fixture README rule 5): the other fields belong to
  // the tab it selects, so the snapshot for Undo is taken on that tab.
  const mode = pending.patch.mode;
  if ((mode === 'explore' || mode === 'refine') && mode !== priorMode) {
    if (!switchMode(mode)) {
      logger.warn('Refine requested without a source render', {
        designId: pending.designId,
      });
    }
  }

  const render = useRenderStore.getState();
  const prior = renderSettingsSnapshot(render.formData);
  const { settings, skipped } = applyRenderSettingsPatch(prior, pending.patch);
  const fields = Object.keys(pending.patch).filter(
    (key) => key !== 'mode' && !skipped.includes(key)
  );

  // The same store write a tap in the settings panel makes; it persists the
  // draft, so the change survives a reload like any other edit.
  render.updateFormData(renderFormPartial(settings, fields, render.formData.prompts));

  const assistant = useAssistantStore.getState();
  assistant.setUndo(pending.undoKey, {
    designId: pending.designId,
    prior,
    fields,
    priorMode: render.formData.mode === priorMode ? null : priorMode,
    undone: false,
  });
  if (skipped.length > 0) {
    logger.info('Settings patch fields skipped by this build', { skipped });
    assistant.addSkippedFields(skipped);
  }
  return true;
}

/**
 * Switch the form's tab the way the panel's mode toggle does. Refine iterates
 * on the render the form was opened on; without one there is nothing to
 * refine and the tab stays.
 */
function switchMode(mode: RenderMode): boolean {
  const render = useRenderStore.getState();
  const source = render.currentContext?.renderId ?? null;
  if (mode === 'refine' && !source) return false;
  render.setRefineMode(mode, mode === 'refine' ? source : null);
  return true;
}

/** Put back the fields a patch changed, as they were before it, then its tab. */
export function undoPatch(key: string): void {
  const assistant = useAssistantStore.getState();
  const undo = assistant.undo[key];
  if (!undo || undo.undone || !isDesignFormLoaded(undo.designId)) return;

  const render = useRenderStore.getState();
  render.updateFormData(
    renderFormPartial(undo.prior, undo.fields, render.formData.prompts)
  );
  if (undo.priorMode) switchMode(undo.priorMode);
  assistant.markUndone(key);
}
